/*
 * ts-search-core.js — Shared Typesense search frontend logic (no build step).
 *
 * Single source of truth for price rendering, money formatting and spec chips,
 * used by header.liquid, search-drawer.liquid and typesense-search.liquid.
 *
 * Preis-Semantik: hit.price / hit.compare_at_price sind IMMER reine NETTO-Euro
 * (Float, laenderunabhaengig). Brutto wird hier per tax-Map (window.__tsCtx.tax)
 * berechnet, mit Integer-Cent-Mathematik analog snippets/price-tax-breakdown.liquid.
 *
 * Exposed as window.TSCore.
 */
(function () {
  'use strict';

  if (window.TSCore) return;

  function ctx() {
    return window.__tsCtx || {};
  }

  function escapeHTML(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ---- Tax map lookup -------------------------------------------------------

  function taxEntry(country) {
    var tax = ctx().tax || {};
    return tax[country] || tax.DEFAULT || { rate: null, mode: 'netto_only' };
  }

  // ---- Money formatting -----------------------------------------------------
  // DETERMINISTIC €-format — deliberately NO window.Shopify.formatMoney
  // dependency (not guaranteed to be loaded; caused missing-€ regressions
  // before). Same formula as snippets/price-list.liquid
  // (__priceTaxBreakdown.update) and the cart interceptor in theme.js.liquid:
  //   '€' + (cents/100).toLocaleString('de-DE', {min/maxFractionDigits: 2})

  function tsFormatPrice(euros) {
    var cents = Math.round(Number(euros) * 100);
    if (!isFinite(cents)) cents = 0;

    var cur = window.Shopify && window.Shopify.currency;
    var active = (cur && cur.active) || 'EUR';

    // Multi-currency markets (e.g. CH/LI -> CHF via Shopify presentment currency):
    // approximate FX conversion. hit.price is EUR-netto; convert with the live
    // Shopify.currency.rate. NOTE: exact parity with Shopify's own server-side
    // price-rounding (whole-CHF etc.) is NOT achievable client-side — this is a
    // deliberate approximation for the search UI only.
    if (active !== 'EUR') {
      var rate = cur && cur.rate ? parseFloat(cur.rate) : 1;
      if (isFinite(rate) && rate > 0) cents = Math.round(cents * rate);
      // The €-prefix format below cannot render CHF correctly; use Intl with
      // the active currency code instead.
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: active }).format(cents / 100);
    }

    // €-Praefix wie die native | money-Ausgabe (z.B. €1.792,80). Intl mit
    // style:'currency' wuerde de-DE den SUFFIX "1.792,80 €" liefern und damit
    // von der nativen Ausgabe abweichen — deshalb Praefix manuell.
    return '€' + (cents / 100).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // German decimal with comma, trailing zeros stripped (10,24 / 5 / 23).
  function deDecimal(n) {
    var num = Number(n);
    if (!isFinite(num)) return String(n);
    var s = (Math.round(num * 100) / 100).toString();
    return s.replace('.', ',');
  }

  // ---- Price rendering ------------------------------------------------------
  // price / compareAtPrice are NETTO euros. opts.compact => one-line autocomplete.
  //
  // FULL mode (/search card) emits the NATIVE price contract — the exact same
  // markup as snippets/price-tax-breakdown.liquid renders server-side:
  //
  //   <div class="price-tax-breakdown[ price-tax-breakdown--sale][ price-tax-breakdown--invert][ price-tax-breakdown--b2b]">
  //   (--sale nur B2C mit UVP; Haendler mit Rabatt bekommen --b2b statt --sale, BA-39)
  //     <div class="brutto-preis" data-mwst="inkl. 20 % USt.">
  //       <span class="brutto-preis__amount">€X</span>
  //       [<s class="compare-price-card">€Y</s>]
  //     </div>
  //     <div class="netto-preis" data-mwst="exkl. USt.">
  //       <span class="netto-preis__amount">€Z</span>
  //       [<s class="compare-price-card">€W</s>]
  //     </div>
  //   </div>
  //
  // The VAT label comes from the data-mwst attribute via CSS ::after (see
  // custom.css [data-mwst]::after) — NO extra label span. Role logic:
  //   B2C breakdown  -> brutto + netto line (brutto = hero via CSS)
  //   B2B + rate     -> same two lines + --invert (netto = hero via CSS)
  //   single_gross   -> brutto line only
  //   netto_only/CH  -> netto line only + --invert (hero styling) + export note
  // Wording is country-aware: AT -> "USt.", everyone else -> "MwSt.".
  //
  // COMPACT mode (header/drawer autocomplete) keeps the legacy .ts-pb--compact
  // one-line markup unchanged — deliberately NOT migrated.

  function vatWord() {
    return (ctx().country === 'AT') ? 'USt.' : 'MwSt.';
  }

  // minQty wie snippets/b2b-price-value.liquid und assets/b2b-pricing.js
  // (validMinQty, P7/P10 BA-13): "5" zaehlt als 5; ohne gueltige minQty >= 1
  // (fehlt, leer, 0, kein Zahlwert) wird die Staffel IGNORIERT. Frueher zaehlte
  // hier eine Staffel ohne minQty als „ab 1“ und rabattierte die Karte.
  function tsValidMinQty(value) {
    if (value == null) return null;
    var text = String(value).trim();
    if (text === '') return null;
    var n = Number(text);
    if (!isFinite(n) || n < 1) return null;
    return n;
  }

  // Gewinnender Rabatt fuer Menge 1: Kunden-Prozentsatz gegen globale Staffeln
  // mit minQty 1. Hoechster Prozentsatz gewinnt, bei Gleichstand die Staffel
  // (Spezifitaet global > customer, Entscheidung D2). Keine Stapelung.
  function tsB2bDiscountPct(c) {
    var best = Number(c.pct);
    if (!isFinite(best) || best < 0) best = 0;
    var tiers = c.tiersGlobal || [];
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      var mq = t ? tsValidMinQty(t.minQty) : null;
      if (mq === null || mq > 1) continue;
      var pct = Number(t.discountPct);
      if (isFinite(pct) && pct >= best) best = pct;
    }
    return Math.min(100, Math.max(0, best));
  }

  // Haendler-Stueckpreis in Euro, gerechnet in ganzen Cent (siehe oben).
  function tsB2bUnitEuro(price, pct) {
    var cents = Math.round(Number(price) * 100);
    return Math.round(cents * (100 - pct) / 100) / 100;
  }

  // Typesense-`filter_by` fuer ALLE Abfragen des Themes (Collection-Raster,
  // Suchseite, Mehr laden, Facetten, Facettensuche, Kategorie-Precompute,
  // Autovervollstaendigung) = optionaler Basisfilter (z. B. Collection-Scope)
  // oder '' (kein Filter). Zwillings-Rueckbau 19.10.2026: der fruehere
  // Sichtbarkeitsfilter `b2b_only:false` (I103, BA-41) ist entfallen, alle
  // B2B-Zwillinge sind geloescht und ein Produkt mit B2B-Merkmal ist ein
  // normales Produkt. Das Index-Feld bleibt bis zum Index-Umbau konstant false.
  // Bleibt die eine Stelle, an der eine kuenftige Klausel fuer alle Abfragen
  // ergaenzt wuerde. Joiner fuer weitere Klauseln ist Typesense `&&`.
  function tsFilterBy(baseFilter) {
    return baseFilter ? String(baseFilter) : '';
  }

  // P7 (BA-13): SSR-Preis einer Haendler-Karte wiederverwenden.
  // Der Suchindex kennt nur Kunden-% und globale Staffeln, nicht die
  // Kategorie- und Produktstaffeln aus den Produkt-Metafeldern. Das SSR
  // markiert Haendler-Karten mit data-b2b-handle + data-b2b-tiers
  // (snippets/price-list.liquid). Traegt die Karte zum Treffer Staffeln
  // ("1"), liefert diese Funktion das SSR-Markup und die Suche rechnet NICHT
  // selbst; bei "0" oder ohne SSR-Karte null (dann rechnet die Suche wie
  // bisher, Grenze: Treffer ohne SSR-Karte auf der Seite).
  function tsSsrB2bPriceHtml(handle) {
    if (!ctx().isB2B || !handle || typeof document === 'undefined') return null;
    var sel = 'price-list[data-b2b-handle="' + (window.CSS && CSS.escape ? CSS.escape(String(handle)) : String(handle).replace(/"/g, '')) + '"]';
    var el = document.querySelector(sel);
    if (!el || el.getAttribute('data-b2b-tiers') === '0') return null;
    return el.outerHTML;
  }

  function tsRenderPriceHtml(price, compareAtPrice, opts) {
    opts = opts || {};
    var c = ctx();
    var compact = !!opts.compact;

    if (price == null || price === '') return '';

    // Preis 0 / negativ / ungueltig (z.B. "Individuelles Angebot", indexiert
    // mit 0): neutrales Label statt "0,00 €" - KEIN Preis, kein Streichpreis,
    // kein Rabatt. Gilt fuer /search-Karte UND Autocomplete-Kompaktpreis.
    if (!(Number(price) > 0)) {
      if (compact) {
        return '<div class="ts-pb ts-pb--compact"><span class="ts-pb__enquiry">Preis auf Anfrage</span></div>';
      }
      return '<div class="price-tax-breakdown"><span class="price-enquiry">Preis auf Anfrage</span></div>';
    }

    var isB2B = !!c.isB2B;

    // ---- B2B-Haendlerpreis (T3) --------------------------------------------
    // Die Karte zeigt IMMER Menge 1, es greifen also nur der Kunden-Prozentsatz
    // und globale Staffeln mit minQty 1. Produkt- und Kategoriestaffeln liegen
    // in Produkt-Metafeldern und sind im Suchindex nicht vorhanden — sie
    // erscheinen erst auf der Produktseite und im Warenkorb. Die Regel (hoechster
    // Prozentsatz gewinnt, keine Stapelung) und die Rundung sind identisch zu
    // snippets/b2b-price.liquid und assets/b2b-pricing.js; gemeinsame Testbasis
    // ist dev/fixtures/b2b-pricing-cases.json (assets/ts-search-core.test.mjs).
    // Rundung in ganzen Cent (I35, 28.09.2026), wie Engine und Liquid:
    //   Math.round(cents x (100 - pct) / 100)
    // Die fruehere Rechnung Math.round(euro x (100 - pct)) / 100 rundete exakte
    // halbe Cent je nach Gleitkomma ab (128,45 minus 10 % ergab 115,60 statt 115,61).
    // BA-39 (I100, 01.10.2026): b2bApplied haelt fest, dass der Streichpreis
    // der LISTENPREIS ist. Dann gibt es wie in snippets/price-tax-breakdown.liquid
    // (b2b_pct > 0) keine Angebotsoptik (--sale, rotes Hero), sondern --b2b:
    // grauer Streich am Netto, Brutto-Zeile ohne Streich und ohne Rot (I48).
    var b2bApplied = false;
    if (isB2B) {
      var b2bPct = tsB2bDiscountPct(c);
      if (b2bPct > 0) {
        b2bApplied = true;
        // Der Listenpreis wird zum Streichpreis: der Haendler soll sehen, was er
        // gegenueber dem Shop-Preis spart. Ein echter UVP tritt dahinter zurueck.
        compareAtPrice = price;
        price = tsB2bUnitEuro(price, b2bPct);
      }
    }

    var entry = taxEntry(c.country || '');
    // BA-18 (F31): Haendler mit Ausfuhr nach CH/LI (Standardadresse) zahlen in
    // der Kasse EUR netto ohne USt, egal welcher Markt gerade aktiv ist (nach
    // der Umschaltung durch assets/b2b-markt.js ist es AT mit 20 %). Deshalb
    // immer der Zweig netto_only mit Ausfuhrhinweis, nie eine Brutto-Zeile.
    if (isB2B && c.b2bExport === true) entry = { rate: 0, mode: 'netto_only' };
    var rate = entry.rate; // Ziel-Land-Steuersatz (auch fuer B2B-Brutto-Sekundaerzeile)
    var isExport = rate === 0; // CH/LI: 0 %, zzgl. Einfuhrabgaben
    var hasRate = rate > 0;

    // Brutto aus dem Netto-Indexpreis hochrechnen (Integer-Cent-Mathematik).
    // NOTE ±1 Cent (DE & alle Nicht-AT-Breakdown-Laender): Shopify speichert
    // dort BRUTTO und die native Karte rechnet brutto -> netto herunter
    // (price-tax-breakdown.liquid: netto = brutto*100/119). Der Typesense-Index
    // liefert dagegen NETTO und wir rechnen hier netto -> brutto hoch. Beide
    // Richtungen runden unabhaengig, dadurch kann das TS-Brutto um ±1 Cent vom
    // SSR-Brutto abweichen. Akzeptierte, dokumentierte Toleranz (AT ist exakt,
    // weil Shopify dort selbst netto speichert).
    var nettoCents = Math.round(Number(price) * 100);
    var bruttoCents = hasRate ? Math.round(nettoCents * (100 + rate) / 100) : nettoCents;

    var hasCompare = compareAtPrice != null && compareAtPrice !== '' && Number(compareAtPrice) > Number(price);
    var cNettoCents = hasCompare ? Math.round(Number(compareAtPrice) * 100) : 0;
    var cBruttoCents = hasCompare && hasRate ? Math.round(cNettoCents * (100 + rate) / 100) : cNettoCents;

    var nettoEur = nettoCents / 100;
    var bruttoEur = bruttoCents / 100;
    var cNettoEur = cNettoCents / 100;
    var cBruttoEur = cBruttoCents / 100;

    var w = vatWord();
    var inklLabel = 'inkl. ' + deDecimal(rate) + ' % ' + w; // "inkl. 20 % USt."
    var exklLabel = 'exkl. ' + w;                               // "exkl. USt."

    function fmt(e) { return tsFormatPrice(e); }

    // Decide the two roles (hero / secondary).
    //   B2B + rate      -> netto hero, brutto secondary
    //   B2C breakdown   -> brutto hero, netto secondary
    //   single_gross    -> brutto hero, no secondary
    //   netto_only/export-> netto hero, no brutto secondary (export hint instead)
    var mode = entry.mode || 'netto_only';
    var heroNetto, heroEur, heroCompareEur, secEur, secWord, showSec;

    if (isB2B && hasRate) {
      heroNetto = true; heroEur = nettoEur; heroCompareEur = cNettoEur;
      secEur = bruttoEur; secWord = 'brutto'; showSec = true;
    } else if (!isB2B && mode === 'breakdown' && hasRate) {
      heroNetto = false; heroEur = bruttoEur; heroCompareEur = cBruttoEur;
      secEur = nettoEur; secWord = 'netto'; showSec = true;
    } else if (!isB2B && mode === 'single_gross' && hasRate) {
      heroNetto = false; heroEur = bruttoEur; heroCompareEur = cBruttoEur;
      showSec = false;
    } else {
      // netto_only (B2B/B2C without positive rate, incl. CH/LI export)
      heroNetto = true; heroEur = nettoEur; heroCompareEur = cNettoEur;
      showSec = false;
    }

    // ---- Compact (autocomplete) — UNCHANGED legacy .ts-pb markup -------------
    // One flowing row: hero amount + short secondary; availability is appended
    // by the caller. No inline VAT-% label and no badge (space-constrained).
    if (compact) {
      var saleCls = hasCompare && !b2bApplied ? ' ts-pb--sale' : '';
      var cHero = '<span class="ts-pb__big">' + fmt(heroEur) + '</span>'
        + (hasCompare ? '<s class="ts-pb__strike">' + fmt(heroCompareEur) + '</s>' : '');
      var cSec;
      if (showSec) {
        cSec = '<span class="ts-pb__sec"><b>' + fmt(secEur) + '</b> ' + secWord + '</span>';
      } else if (isExport) {
        cSec = '<span class="ts-pb__sec ts-pb__export">zzgl. Einfuhr</span>';
      } else {
        cSec = '<span class="ts-pb__sec">' + (heroNetto ? 'netto' : 'brutto') + '</span>';
      }
      return '<div class="ts-pb ts-pb--compact' + saleCls + '">' + cHero + cSec + '</div>';
    }

    // ---- Full (/search card) — NATIVE price-tax-breakdown markup -------------
    // Identical classes + data-mwst attribute as price-tax-breakdown.liquid.
    // VAT wording comes from data-mwst via CSS ::after (no label span). The
    // netto compare-strike is rendered for structural parity with SSR; the
    // card CSS hides it in the --sale state (hero carries the compare-at).
    function bruttoLine() {
      return '<div class="brutto-preis" data-mwst="' + escapeHTML(inklLabel) + '">'
        + '<span class="brutto-preis__amount">' + fmt(bruttoEur) + '</span>'
        + (hasCompare ? '<s class="compare-price-card">' + fmt(cBruttoEur) + '</s>' : '')
        + '</div>';
    }
    function nettoLine() {
      return '<div class="netto-preis" data-mwst="' + escapeHTML(exklLabel) + '">'
        + '<span class="netto-preis__amount">' + fmt(nettoEur) + '</span>'
        + (hasCompare ? '<s class="compare-price-card">' + fmt(cNettoEur) + '</s>' : '')
        + '</div>';
    }

    var lines;
    var invert = false;
    if (showSec) {
      // Two lines, always brutto first in DOM (like the Liquid snippet);
      // the B2B hero swap happens via --invert CSS (order: -1), not DOM order.
      lines = bruttoLine() + nettoLine();
      invert = heroNetto;
    } else if (heroNetto) {
      // netto_only / export (CH/LI): single netto line, hero-styled via --invert.
      lines = nettoLine();
      invert = true;
      if (isExport) lines += '<div class="price-export-note">zzgl. Einfuhrabgaben</div>';
    } else {
      // single_gross: single brutto line (hero by default).
      lines = bruttoLine();
    }

    var rootCls = 'price-tax-breakdown'
      + (hasCompare && !b2bApplied ? ' price-tax-breakdown--sale' : '')
      + (invert ? ' price-tax-breakdown--invert' : '')
      + (hasCompare && b2bApplied ? ' price-tax-breakdown--b2b' : '');
    return '<div class="' + rootCls + '">' + lines + '</div>';
  }

  // ---- Corner "−X%" discount badge -------------------------------------------
  // Single discount indicator for the /search card — same classes AND same
  // formula as the native badge (snippets/product-badges.liquid):
  //   X = round((1 - price/compare_at) * 100)
  // The percentage is tax-scale-invariant (netto index prices give the same
  // ratio as brutto SSR prices), so SSR and TS are mathematically identical
  // without any brutto factor. Native renders <on-sale-badge> (web component,
  // SSR-only exception); TS renders a plain <span> with identical classes.
  // Returns '' when there is no real positive discount (or prices hidden).

  function tsDiscountBadgeHTML(price, compareAtPrice) {
    if (!(Number(price) > 0)) return '';

    // Haendler (BA-37, 01.10.2026): kein Rabatt-Badge, weder der UVP-Rabatt
    // (I41) noch das fruehere „Händlerpreis −x %“, wie
    // snippets/product-badges.liquid. Die Leiste im Header sagt, dass
    // Haendlerpreise gelten. Der Badge-Platz bleibt dem Ausverkauft-Badge.
    if (ctx().isB2B) return '';

    var hasCompare = compareAtPrice != null && compareAtPrice !== '' && Number(compareAtPrice) > Number(price);
    if (!hasCompare) return '';

    var pct = Math.round((Number(compareAtPrice) - Number(price)) * 100 / Number(compareAtPrice));
    if (pct <= 0) return '';

    return '<span class="badge badge--on-sale">−' + pct + '%</span>';
  }

  // ---- Shopify-CDN responsive image srcset ----------------------------------
  // hit.image_url is a cdn.shopify.com URL; Shopify's image CDN resizes via the
  // `width` query param. Appends `?width=N` / `&width=N` depending on whether
  // the URL already carries query params. Returns '' for non-Shopify URLs so
  // the caller can fall back to a plain src.

  function tsImageSrcset(url, widths) {
    if (!url || String(url).indexOf('cdn.shopify.com') === -1) return '';
    var sep = String(url).indexOf('?') === -1 ? '?' : '&';
    return (widths || [200, 400, 600, 800]).map(function (w) {
      return url + sep + 'width=' + w + ' ' + w + 'w';
    }).join(', ');
  }

  // ---- Display-world conversion (price filters) -----------------------------
  // Der Index filtert auf NETTO-EUR, der Kunde denkt aber in der ANGEZEIGTEN
  // Preiswelt (Brutto in Breakdown-Laendern, CHF-konvertiert in CH/LI).
  // displayToNetto/nettoToDisplay uebersetzen zwischen beiden Welten;
  // B2B und netto_only-EUR-Laender: Identitaet.

  function displayContext() {
    var c = ctx();
    var entry = taxEntry(c.country || '');
    var grossMode = entry.mode === 'breakdown' || entry.mode === 'single_gross';
    var rate = (!c.isB2B && grossMode && entry.rate > 0) ? entry.rate : 0;
    var fx = 1;
    var currency = 'EUR';
    var cur = window.Shopify && window.Shopify.currency;
    if (cur && cur.active && cur.active !== 'EUR') {
      var r = parseFloat(cur.rate);
      if (isFinite(r) && r > 0) { fx = r; currency = cur.active; }
    }
    return {
      rate: rate,
      fx: fx,
      currency: currency,
      taxLabel: rate > 0 ? 'inkl. MwSt.' : 'exkl. MwSt.'
    };
  }

  function displayToNetto(display) {
    var k = displayContext();
    return Number(display) / k.fx / (1 + k.rate / 100);
  }

  function nettoToDisplay(netto) {
    var k = displayContext();
    return Number(netto) * (1 + k.rate / 100) * k.fx;
  }

  // Formatiert einen Betrag, der BEREITS in der Anzeigewelt liegt (keine
  // erneute FX-Konvertierung wie in tsFormatPrice). Ganze Betraege ohne
  // Nachkommastellen (Filter-Chips, Buckets).
  function formatDisplayAmount(display) {
    var k = displayContext();
    var n = Number(display);
    if (!isFinite(n)) n = 0;
    var isInt = Math.abs(n - Math.round(n)) < 0.005;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: k.currency,
      minimumFractionDigits: isInt ? 0 : 2,
      maximumFractionDigits: isInt ? 0 : 2
    }).format(n);
  }

  // ---- Spec highlight chips (unified card contract, slot c) -----------------
  // Up to 3, priority: power_watt -> power_kw -> capacity_kwh -> phases
  //                    -> material -> efficiency_percent
  // Emits the shared card classes (.product-card__specs/__spec-chip). The
  // native counterpart is snippets/product-card-specs.liquid — currently an
  // empty min-height container because the typed spec fields only exist in
  // the Typesense index (server-side extraction), not as Shopify metafields.

  function hasVal(v) {
    return v != null && v !== '';
  }

  // Spec-Chips vorerst AUS (03.10.): der Admin-Extraktor liefert teils falsche
  // Werte (Ah als kWh gelesen, Bereiche zusammengeklebt, Paletten-W, Victron-kW).
  // Bis zum Extraktor-Fix nur die leere Chip-Zeile wie auf der nativen Karte
  // (snippets/product-card-specs.liquid), damit der Kartenrhythmus gleich bleibt.
  var SPEC_CHIPS_ENABLED = false;

  function tsBuildSpecChips(hit) {
    if (!hit) return '';
    if (!SPEC_CHIPS_ENABLED) return '<div class="product-card__specs" aria-hidden="true"></div>';
    var chips = [];
    function add(label) {
      if (label && chips.length < 3) chips.push(label);
    }

    if (hasVal(hit.power_watt)) add(deDecimal(hit.power_watt) + ' W');
    if (hasVal(hit.power_kw)) add(deDecimal(hit.power_kw) + ' kW');
    if (hasVal(hit.capacity_kwh)) add(deDecimal(hit.capacity_kwh) + ' kWh');
    if (hasVal(hit.phases)) {
      var ph = parseInt(hit.phases, 10);
      if (ph === 3) add('3-phasig');
      else if (ph === 1) add('1-phasig');
    }
    if (hasVal(hit.material)) add(String(hit.material));
    if (hasVal(hit.efficiency_percent)) add(deDecimal(hit.efficiency_percent) + ' % Wirkungsgrad');

    if (!chips.length) return '';
    return '<div class="product-card__specs">'
      + chips.map(function (t) { return '<span class="product-card__spec-chip">' + escapeHTML(t) + '</span>'; }).join('')
      + '</div>';
  }

  window.TSCore = {
    ctx: ctx,
    filterBy: tsFilterBy,
    escapeHTML: escapeHTML,
    formatPrice: tsFormatPrice,
    renderPriceHtml: tsRenderPriceHtml,
    ssrB2bPriceHtml: tsSsrB2bPriceHtml,
    validMinQty: tsValidMinQty,
    b2bDiscountPct: tsB2bDiscountPct,
    b2bUnitEuro: tsB2bUnitEuro,
    discountBadgeHTML: tsDiscountBadgeHTML,
    buildSpecChips: tsBuildSpecChips,
    imageSrcset: tsImageSrcset,
    displayContext: displayContext,
    displayToNetto: displayToNetto,
    nettoToDisplay: nettoToDisplay,
    formatDisplayAmount: formatDisplayAmount
  };
})();
